import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { get } from "@/lib/api";
import { nok, fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAssistantOptional } from "@/context/AssistantContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PageHeader, KpiTile, KpiGrid } from "@/components/common/Page";
import { LoadingState, ErrorState } from "@/components/common/States";
import { Money, Panel, Provenance } from "@/components/finance/FinanceBits";
import { CashflowChart, RevenueChart } from "@/components/finance/Charts";
import { compactKr } from "@/components/finance/money";

export default function ForecastPage() {
  const assistant = useAssistantOptional();
  const [pipeline, setPipeline] = useState(true);
  const q = useQuery({ queryKey: ["finance", "forecast"], queryFn: () => get("/finance/forecast", { months: 12, weeks: 13 }) });
  if (q.isLoading) return <LoadingState className="py-32" />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const cf = q.data.cashflow;
  const rev = q.data.revenue;
  const future = rev.months.filter((m) => !m.is_past);
  const total12 = future.reduce((s, m) => s + m.contracted + (pipeline ? m.pipeline_weighted : 0), 0);
  return (
    <div data-testid="forecast-page">
      <PageHeader
        title="Prognose"
        subtitle="Kontantstrøm uke for uke og inntekt måned for måned – fra kontrakter, åpne fakturaer, leverandørforfall og vektet pipeline. Alt her er prognose; faktiske tall er merket."
        actions={assistant && <Button variant="outline" onClick={() => assistant.ask("Vurder kontantstrømprognosen for de neste 13 ukene og inntektsprognosen for 12 måneder – hvor er risikoen, og hva bør vi gjøre?")} data-testid="forecast-ask-ai"><Sparkles /> Vurder med AI</Button>}
        testId="forecast-header"
      />

      <KpiGrid className="mt-8" cols="grid-cols-2 md:grid-cols-4" testId="forecast-kpis">
        <KpiTile label="Saldo i dag" value={compactKr(cf.opening_balance)} hint="åpningssaldo (innstilling)" testId="forecast-kpi-opening" />
        <KpiTile label={`Laveste punkt · ${cf.min_week}`} value={compactKr(cf.min_balance)} tone={cf.min_balance < 0 ? "danger" : cf.min_balance < cf.opening_balance * 0.5 ? "warning" : "default"} hint="prognose" testId="forecast-kpi-min" />
        <KpiTile label="Saldo etter 13 uker" value={compactKr(cf.closing_balance)} hint={`${compactKr(cf.total_inflow)} inn · ${compactKr(cf.total_outflow)} ut`} testId="forecast-kpi-closing" />
        <KpiTile label="Inntekt neste 12 mnd" value={compactKr(total12)} hint={pipeline ? `${compactKr(rev.summary.contracted_12m)} kontrakt + ${compactKr(rev.summary.pipeline_weighted_12m)} pipeline` : "kun kontraktsfestet"} testId="forecast-kpi-revenue" />
      </KpiGrid>

      <Panel className="mt-10" title="Kontantstrøm neste 13 uker" meta="Innbetalinger fra åpne fakturaer på forfall (forfalte innen 7 dager) · planlagt fakturering fra aktive kontrakter · leverandørforfall · faste kostnader" testId="forecast-cashflow">
        <CashflowChart weeks={cf.weeks} height={300} />
        <div className="mt-6 overflow-x-auto scrollbar-thin">
          <table className="w-full text-[12.5px]" data-testid="forecast-cashflow-table">
            <thead>
              <tr className="text-left text-[11.5px] text-muted-foreground">
                <th className="py-1.5 pr-3 font-medium">Uke</th><th className="py-1.5 pr-3 font-medium">Periode</th>
                <th className="py-1.5 pr-3 text-right font-medium">Fra fakturaer</th><th className="py-1.5 pr-3 text-right font-medium">Planlagt fakt.</th><th className="py-1.5 pr-3 text-right font-medium">Leverandører</th><th className="py-1.5 pr-3 text-right font-medium">Faste</th><th className="py-1.5 pr-3 text-right font-medium">Netto</th><th className="py-1.5 text-right font-medium">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {cf.weeks.map((w) => (
                <tr key={w.week} className={cn("border-t border-border/60", w.week === cf.min_week && "bg-amber-500/[0.06]")} data-testid={`forecast-week-${w.week}`}>
                  <td className="py-1.5 pr-3 font-medium tabular">{w.week}{w.is_current ? <span className="ml-1 text-[10.5px] text-primary">nå</span> : ""}</td>
                  <td className="py-1.5 pr-3 text-muted-foreground tabular">{fmtDate(w.start, "d. MMM")} – {fmtDate(w.end, "d. MMM")}</td>
                  <td className="py-1.5 pr-3 text-right"><Money v={w.in_receivables} muted={!w.in_receivables} /></td>
                  <td className="py-1.5 pr-3 text-right"><Money v={w.in_planned} muted={!w.in_planned} /></td>
                  <td className="py-1.5 pr-3 text-right"><Money v={w.out_suppliers ? -w.out_suppliers : 0} muted={!w.out_suppliers} /></td>
                  <td className="py-1.5 pr-3 text-right"><Money v={-w.out_fixed} className="text-muted-foreground" /></td>
                  <td className="py-1.5 pr-3 text-right"><Money v={w.net} sign className={cn("font-medium", w.net >= 0 ? "text-emerald-700 dark:text-emerald-300" : "")} /></td>
                  <td className="py-1.5 text-right font-medium"><Money v={w.balance} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[12px] text-muted-foreground">
          <Provenance kind="prognose" />
          <span>Faste kostnader {nok(cf.assumptions.fixed_costs_monthly)}/mnd (lønn, depot, drift – innstilling)</span>
          <span>Planlagt fakturering forutsetter betaling på forfall</span>
          <span>Ingen bankintegrasjon – saldo er modellert fra åpningssaldo</span>
        </div>
      </Panel>

      <Panel
        className="mt-12"
        title="Inntekt – siste 6 og neste 12 måneder"
        meta={`${rev.summary.active_contracts} aktive/signerte kontrakter · ${rev.summary.open_leads} åpne henvendelser vektet med fase-sannsynlighet (ny 10 % → forhandling 70 %)`}
        action={
          <label className="inline-flex items-center gap-2 text-[12.5px]" data-testid="forecast-pipeline-toggle">
            <Switch checked={pipeline} onCheckedChange={setPipeline} aria-label="Inkluder vektet pipeline" />
            <Label className="cursor-pointer font-normal text-muted-foreground">Inkluder pipeline</Label>
          </label>
        }
        testId="forecast-revenue"
      >
        <RevenueChart months={rev.months} height={280} showPipeline={pipeline} />
        <div className="mt-6 overflow-x-auto scrollbar-thin">
          <table className="w-full text-[12.5px]" data-testid="forecast-revenue-table">
            <thead>
              <tr className="text-left text-[11.5px] text-muted-foreground"><th className="py-1.5 pr-3 font-medium">Måned</th><th className="py-1.5 pr-3 text-right font-medium">Fakturert (faktisk)</th><th className="py-1.5 pr-3 text-right font-medium">Kontrakt (prognose)</th>{pipeline && <th className="py-1.5 pr-3 text-right font-medium">Pipeline (vektet)</th>}<th className="py-1.5 pr-3 text-right font-medium">Sum</th><th className="py-1.5 text-right font-medium">Kontrakter</th></tr>
            </thead>
            <tbody>
              {rev.months.map((m) => (
                <tr key={m.month} className={cn("border-t border-border/60", m.is_current && "bg-primary/[0.05]")} data-testid={`forecast-month-${m.month}`}>
                  <td className="py-1.5 pr-3 font-medium capitalize tabular">{m.label}{m.is_current ? <span className="ml-1 text-[10.5px] text-primary">nå</span> : ""}</td>
                  <td className="py-1.5 pr-3 text-right"><Money v={m.invoiced} muted={!m.invoiced} /></td>
                  <td className="py-1.5 pr-3 text-right"><Money v={m.contracted} muted={!m.contracted} /></td>
                  {pipeline && <td className="py-1.5 pr-3 text-right"><Money v={m.pipeline_weighted} muted={!m.pipeline_weighted} /></td>}
                  <td className="py-1.5 pr-3 text-right font-medium"><Money v={m.invoiced + m.contracted + (pipeline ? m.pipeline_weighted : 0)} /></td>
                  <td className="py-1.5 text-right text-muted-foreground tabular">{m.contracts || "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[12px] text-muted-foreground"><Provenance kind="faktisk" /> fakturert eks. mva <Provenance kind="prognose" /> kontraktsfestet leie per måned <Provenance kind="pipeline" /> estimert verdi × sannsynlighet fordelt på varighet</div>
      </Panel>
    </div>
  );
}
